import {
  NR2J_QUOTE_EDITOR_GATE_KEYS,
  NR2J_QUOTE_ORG_GATE_KEYS,
  computeContractSendGateMissingClient,
  computeContractSignatoryGateMissing,
  computeConversionMissing,
  computeInvoiceBuyerGateMissing,
  computeOrgSendGateMissing,
} from './nr2jConstants.js';
import { extractNr2jContractGateMissing } from './nr2jContractGateUi.js';

export const NR2J_CONTINUE_INTENTS = Object.freeze({
  QUOTE_TO_CONTRACT: 'quoteToContract',
  QUOTE_TO_INVOICE: 'quoteToInvoice',
  QUOTE_SEND: 'quoteSend',
  CONTRACT_SEND: 'contractSend',
  CONTRACT_SUBMIT: 'contractSubmit',
  INVOICE_ISSUE: 'invoiceIssue',
  INVOICE_SEND: 'invoiceSend',
  CONTRACT_FROM_QUOTE_EDITOR: 'contractFromQuoteEditor',
});

export function extractNr2jErrorCode(error) {
  return error?.code
    || error?.data?.error?.code
    || error?.response?.data?.error?.code
    || null;
}

export function extractNr2jErrorMissing(error) {
  const raw = error?.data?.error?.missing
    || error?.response?.data?.error?.missing
    || error?.missing;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

const SIGNATORY_KEYS = new Set(['signatoryName', 'signatoryEmail', 'signatoryFunction']);
const CONVERSION_KEYS = new Set([
  'identity', 'siren', 'siret', 'email', 'adresse', 'codePostal', 'ville', 'pays', 'primaryContact',
]);
const CONTRACT_OFFER_KEYS = new Set(['templateVersionId', 'lines']);

export function missingKeysToBlocks(missingKeys = []) {
  const keys = missingKeys || [];
  const blocks = new Set();
  if (keys.some((k) => SIGNATORY_KEYS.has(k) || k === 'CONTRACT_SIGNATORY_REQUIRED')) {
    blocks.add('signatory');
  }
  if (keys.some((k) => NR2J_QUOTE_ORG_GATE_KEYS.has(k) || CONVERSION_KEYS.has(k)
    || ['siren', 'siret', 'siren|siret|tvaIntracommunautaire'].includes(k))) {
    blocks.add('orgLegal');
  }
  if (keys.some((k) => NR2J_QUOTE_EDITOR_GATE_KEYS.has(k))) {
    blocks.add('quoteMeta');
  }
  if (keys.some((k) => CONTRACT_OFFER_KEYS.has(k))) {
    blocks.add('contractOffer');
  }
  if (keys.includes('primaryContact')) {
    blocks.add('primaryContact');
  }
  return [...blocks];
}

export function buildContinueRequirements({
  intent,
  missingKeys = [],
  org,
  contacts = [],
  quoteId,
  versionId,
  quoteMeta,
  signatoryContactId,
  contractId,
  forceSignatory = false,
} = {}) {
  const keys = [...new Set((missingKeys || []).filter(Boolean))];
  if (forceSignatory && !keys.some((k) => SIGNATORY_KEYS.has(k))) {
    keys.push('signatoryName', 'signatoryEmail', 'signatoryFunction');
  }
  const blocks = missingKeysToBlocks(keys);
  if (forceSignatory && !blocks.includes('signatory')) blocks.unshift('signatory');

  const titles = {
    [NR2J_CONTINUE_INTENTS.QUOTE_TO_CONTRACT]: 'Informations nécessaires pour créer le contrat',
    [NR2J_CONTINUE_INTENTS.QUOTE_TO_INVOICE]: 'Informations nécessaires pour créer la facture',
    [NR2J_CONTINUE_INTENTS.QUOTE_SEND]: 'Informations nécessaires pour envoyer le devis',
    [NR2J_CONTINUE_INTENTS.CONTRACT_SEND]: 'Informations nécessaires pour envoyer le contrat',
    [NR2J_CONTINUE_INTENTS.CONTRACT_SUBMIT]: 'Informations nécessaires pour soumettre le contrat',
    [NR2J_CONTINUE_INTENTS.INVOICE_ISSUE]: 'Informations nécessaires pour émettre la facture',
    [NR2J_CONTINUE_INTENTS.INVOICE_SEND]: 'Informations nécessaires pour envoyer la facture',
    [NR2J_CONTINUE_INTENTS.CONTRACT_FROM_QUOTE_EDITOR]: 'Informations nécessaires pour créer le contrat',
  };

  return {
    intent,
    title: titles[intent] || 'Informations nécessaires pour continuer',
    missingKeys: keys,
    blocks,
    org: org || null,
    contacts: contacts || [],
    quoteId: quoteId || null,
    versionId: versionId || null,
    quoteMeta: quoteMeta || null,
    signatoryContactId: signatoryContactId || '',
    contractId: contractId || null,
  };
}

export function preflightQuoteSend({ org, contacts, validUntil, contactId }) {
  const missing = [...computeOrgSendGateMissing(org, contacts)];
  if (!validUntil) missing.push('validUntil');
  const hasRecipient = contactId
    ? (contacts || []).some((c) => c.id === contactId && c.email && /@/.test(String(c.email)))
    : false;
  if (!hasRecipient) {
    const orgEmail = org?.emailFacturation || org?.emailPrincipal;
    if (!orgEmail || !/@/.test(String(orgEmail))) missing.push('recipientEmail');
  }
  return missing.length
    ? buildContinueRequirements({
      intent: NR2J_CONTINUE_INTENTS.QUOTE_SEND,
      missingKeys: missing,
      org,
      contacts,
      quoteMeta: { validUntil, contactId },
    })
    : null;
}

export function preflightQuoteToContract({ org, contacts, quote, version }) {
  if (!version || version.status !== 'ACCEPTED') return null;
  if (version.contactId) {
    const contact = (contacts || []).find((c) => c.id === version.contactId);
    const sigMissing = computeContractSignatoryGateMissing(contact);
    if (sigMissing.length) {
      return buildContinueRequirements({
        intent: NR2J_CONTINUE_INTENTS.QUOTE_TO_CONTRACT,
        missingKeys: sigMissing,
        org,
        contacts,
        quoteId: quote?.id,
        versionId: version.id,
        signatoryContactId: version.contactId,
      });
    }
    return null;
  }
  return buildContinueRequirements({
    intent: NR2J_CONTINUE_INTENTS.QUOTE_TO_CONTRACT,
    missingKeys: ['signatoryName', 'signatoryEmail', 'signatoryFunction'],
    org,
    contacts,
    quoteId: quote?.id,
    versionId: version.id,
    forceSignatory: true,
  });
}

export function preflightQuoteToInvoice({ org, contacts }) {
  const missing = computeConversionMissing(org, contacts, { forInvoice: true });
  if (!missing.length) return null;
  return buildContinueRequirements({
    intent: NR2J_CONTINUE_INTENTS.QUOTE_TO_INVOICE,
    missingKeys: missing,
    org,
    contacts,
  });
}

export function preflightInvoiceAction({ org, contacts, contactId, intent }) {
  const contact = (contacts || []).find((c) => c.id === contactId) || null;
  const missing = computeInvoiceBuyerGateMissing(org, contacts, contact);
  if (!missing.length) return null;
  return buildContinueRequirements({
    intent,
    missingKeys: missing,
    org,
    contacts,
  });
}

export function preflightContractSend({ org, contacts, signatoryContactId, contract, version }) {
  const contact = (contacts || []).find((c) => c.id === signatoryContactId) || null;
  const missing = computeContractSendGateMissingClient(org, contact, {
    ...version,
    lines: contract?.currentVersion?.lines || version?.lines,
    templateVersionId: version?.templateVersionId,
  });
  if (!missing.length) return null;
  return buildContinueRequirements({
    intent: NR2J_CONTINUE_INTENTS.CONTRACT_SEND,
    missingKeys: missing,
    org,
    contacts,
    signatoryContactId,
    contractId: contract?.id,
  });
}

export function requirementsFromError(error, context = {}) {
  const code = extractNr2jErrorCode(error);
  const missing = extractNr2jErrorMissing(error);
  const contractMissing = extractNr2jContractGateMissing(error);
  const keys = missing.length ? missing : contractMissing;

  if (code === 'CONTRACT_SIGNATORY_REQUIRED') {
    return buildContinueRequirements({
      intent: context.intent || NR2J_CONTINUE_INTENTS.QUOTE_TO_CONTRACT,
      missingKeys: ['signatoryName', 'signatoryEmail', 'signatoryFunction'],
      forceSignatory: true,
      ...context,
    });
  }
  if (code === 'CONTRACT_SIGNATORY_INCOMPLETE' && keys.length) {
    return buildContinueRequirements({
      intent: context.intent || NR2J_CONTINUE_INTENTS.QUOTE_TO_CONTRACT,
      missingKeys: keys,
      forceSignatory: true,
      ...context,
    });
  }
  if (code === 'CONVERSION_INCOMPLETE' && keys.length) {
    return buildContinueRequirements({
      intent: context.intent || NR2J_CONTINUE_INTENTS.QUOTE_TO_INVOICE,
      missingKeys: keys,
      ...context,
    });
  }
  if ((code === 'QUOTE_SEND_GATE' || code === 'INVOICE_ISSUE_GATE' || code === 'INVOICE_SEND_GATE'
    || code === 'CONTRACT_SEND_GATE') && keys.length) {
    return buildContinueRequirements({
      intent: context.intent,
      missingKeys: keys,
      ...context,
    });
  }
  return null;
}
